import {observe} from '@github-ui/selector-observer'
import {on} from 'delegated-events'

observe('.js-pricing-testimonial-logo', () => {
  for (const el of document.querySelectorAll<HTMLElement>('.js-pricing-testimonial-logo')) {
    el.addEventListener('mouseenter', showTestimonial)
  }
})

on('click', '.js-pricing-testimonial-logo', e => {
  showTestimonial(e)
})

interface PlanSeats {
  [key: string]: Plan | undefined
  free: Plan
  business: Plan
  business_plus: Plan
}

interface Plan {
  [key: string]: number | string | boolean | undefined
  cost: number
}
const plans: PlanSeats = {
  free: {cost: 0},
  business: {cost: 0},
  business_plus: {cost: 0},
}

const DEFAULT_SEATS = 5

observe('#plan-costs-json', el => {
  const planCosts = JSON.parse(el.getAttribute('data-plan-costs') || '{}')

  for (const key of Object.keys(planCosts)) {
    plans[key] = planCosts[key]
  }

  showYearlyMessages()
  showPrice()
  updateCostSuffix()
  const forcedPlanCard = document.querySelector<HTMLElement>('.js-pricing-plan.js-forced-recommended-plan')
  if (!forcedPlanCard) {
    highlightRecommendedPlan(DEFAULT_SEATS)
  }
  updateUpgradePath()
})

// Show yearly billing cost if yearly radio is checked
on('click', 'input[type="radio"][name="billing_cycle"]', () => {
  showYearlyMessages()
  showPrice()
  updateUpgradePath()
  updateCostSuffix()
  updateAriaChecked()
})

// Show the price to the user
function showPrice() {
  for (const el of document.querySelectorAll<HTMLElement>('.js-computed-value')) {
    /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
    const plan = el.getAttribute('data-plan')!.toString()
    const planData = plans[plan]
    const planCost = Number(planData?.['price'] || 0)
    el.textContent = Number.isInteger(planCost) ? planCost.toString() : planCost.toFixed(2)
  }
}

function updateAriaChecked() {
  /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
  const checkedInput = document.querySelector<HTMLInputElement>('input[type="radio"][name="billing_cycle"]:checked')!
  /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
  const uncheckedInput = document.querySelector<HTMLInputElement>(
    'input[type="radio"][name="billing_cycle"]:not(:checked)',
  )!

  checkedInput.setAttribute('aria-checked', 'true')
  checkedInput.setAttribute('checked', '')
  uncheckedInput.setAttribute('aria-checked', 'false')
  uncheckedInput.removeAttribute('checked')
}

function getBillingCycle() {
  // Select the checked radio switch input and pass its value...
  /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
  const checkedInput = document.querySelector<HTMLInputElement>('input[type="radio"][name="billing_cycle"]:checked')!

  return checkedInput ? checkedInput.value.trim() : 'monthly'
}

function highlightRecommendedPlan(value: number) {
  const recommendedPlanClassList = ['pricing-recommended-plan']

  for (const planCard of document.querySelectorAll('.js-pricing-plan')) {
    planCard.classList.remove(...recommendedPlanClassList)
    const planMinSeats = Number(planCard.getAttribute('data-min-seats'))
    const planMaxSeats = Number(planCard.getAttribute('data-max-seats'))

    if (planMinSeats <= value && value <= planMaxSeats) {
      /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
      const planCardCaptionElement = planCard.querySelectorAll<HTMLElement>('.js-recommended-plan-caption')[0]!
      planCardCaptionElement.hidden = false
      planCard.classList.add(...recommendedPlanClassList)
    }
  }
}

// Update the cost suffix when the seats or billing cycle controls are engaged, using the correct billing cycle
function updateCostSuffix() {
  for (const element of document.querySelectorAll<HTMLElement>('.js-pricing-cost-suffix')) {
    if (getBillingCycle() === 'monthly' || element.classList.contains('js-monthly-suffix')) {
      element.textContent = 'per user/month'
    } else if (getBillingCycle() === 'yearly') {
      const plan = element.getAttribute('data-plan')
      if (plan?.toLowerCase() === 'free') {
        element.textContent = 'per month forever'
      } else {
        element.textContent = 'per user/month'
      }
    }
  }
}

// Update the URL parameters on upgrade CTAs, using the correct billing cycle
function updateUpgradePath() {
  const paramValue = getBillingCycle() === 'yearly' ? 'year' : 'month'

  for (const element of document.querySelectorAll<HTMLElement>('a.js-pricing-upgrade-path')) {
    /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
    const upgradePath = element.getAttribute('href')!

    if (upgradePath.includes('plan_duration=month')) {
      element.setAttribute('href', upgradePath.replace('plan_duration=month', `plan_duration=${paramValue}`))
    } else if (upgradePath.includes('plan_duration=year')) {
      element.setAttribute('href', upgradePath.replace('plan_duration=year', `plan_duration=${paramValue}`))
    } else {
      element.setAttribute('href', upgradePath.concat('&', `plan_duration=${paramValue}`))
    }
  }
}

function showTestimonial(e: MouseEvent) {
  const logo = e.currentTarget
  if (!(logo instanceof HTMLElement)) return
  if (logo.classList.contains('pricing-testimonial-logo-active')) return

  const targetQuote = getPricingTestimonialQuoteForLogo(logo)
  const targetImage = getPricingTestimonialImgForLogo(logo)

  const previousActiveLogo = document.querySelector<HTMLElement>('.pricing-testimonial-logo-active')
  if (previousActiveLogo) {
    previousActiveLogo.classList.remove('pricing-testimonial-logo-active')
    previousActiveLogo.setAttribute('aria-selected', 'false')
  }

  logo.classList.add('pricing-testimonial-logo-active')
  logo.setAttribute('aria-selected', 'true')

  for (const el of document.querySelectorAll('.pricing-testimonial-content-active')) {
    el.classList.remove('pricing-testimonial-content-active')
  }

  targetQuote.classList.add('pricing-testimonial-content-active')
  targetImage.classList.add('pricing-testimonial-content-active')
}

function getPricingTestimonialQuoteForLogo(logo: HTMLElement): HTMLElement {
  const testimonialIndex = getPricingTestimonialLogoIndex(logo)
  const testimonials = Array.from(document.querySelectorAll<HTMLElement>('.js-pricing-testimonial-quote'))
  /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
  return testimonials[testimonialIndex]!
}

function getPricingTestimonialImgForLogo(logo: HTMLElement): HTMLElement {
  const testimonialIndex = getPricingTestimonialLogoIndex(logo)
  const testimonials = Array.from(document.querySelectorAll<HTMLElement>('.js-pricing-testimonial-img'))
  /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
  return testimonials[testimonialIndex]!
}

function getPricingTestimonialLogoIndex(logo: HTMLElement): number {
  const logos = Array.from(document.querySelectorAll('.js-pricing-testimonial-logo'))
  return logos.indexOf(logo)
}

function displaySelectedPlanFeatures(selectedPlan: string) {
  for (const el of document.querySelectorAll<HTMLElement>('.js-compare-features-item')) {
    if (el.getAttribute('data-plan') === selectedPlan) {
      el.style.display = 'block'
    } else {
      el.style.display = 'none'
    }
  }
}

function showYearlyMessages() {
  for (const message of document.querySelectorAll<HTMLElement>('.js-monthly-cost')) {
    message.hidden = getBillingCycle() === 'yearly'
  }

  for (const message of document.querySelectorAll<HTMLElement>('.js-yearly-cost')) {
    message.hidden = getBillingCycle() === 'monthly'
  }
}

observe('.js-compare-features-selected', e => {
  /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
  displaySelectedPlanFeatures(e.getAttribute('data-default-plan')!)
})

on('click', '.js-compare-features-nav', function (e) {
  e.preventDefault()
  const plan = e.currentTarget.getAttribute('data-plan')
  const selectedTitle = document.querySelector<HTMLElement>('.js-compare-features-selected')

  if (selectedTitle && plan !== null) {
    displaySelectedPlanFeatures(plan)
    selectedTitle.textContent = plan
  }
})
